import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import api from '../../../api/axios';

const TestCallModal = ({ isOpen, onClose, agent }) => {
  const [formData, setFormData] = useState({
    testPhoneNumber: '',
    customerName: '',
    orderId: '',
    emailId: '',
    registeredMobileNumber: '',
    productBought: '',
    orderDate: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [balanceData, setBalanceData] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen) return;
    setBalanceLoading(true);
    api.get('/api/v1/dashboard/balance')
      .then(res => setBalanceData(res?.data?.data || null))
      .catch(() => setBalanceData(null))
      .finally(() => setBalanceLoading(false));
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (typeof fbq === 'function') fbq('trackCustom', 'test-agent-event', { content_category: 'AgentPerformancePage' });
    window.dataLayer?.push({ event: 'test_agent_event' });
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setFormData({
        testPhoneNumber: '',
        customerName: '',
        orderId: '',
        emailId: '',
        registeredMobileNumber: '',
        productBought: '',
        orderDate: ''
      });
      onClose();
    }, 2000);
  };

  if (!isOpen) return null;

  const hasBalance = balanceData && balanceData.remainingMinutes > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <h2 className="text-lg md:text-xl font-semibold text-foreground">Test Call</h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-muted transition-colors"
            type="button"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Loading */}
        {balanceLoading && (
          <div className="p-8 flex items-center justify-center">
            <p className="text-sm text-muted-foreground">Checking balance...</p>
          </div>
        )}

        {/* Insufficient Balance */}
        {!balanceLoading && !hasBalance && (
          <div className="p-6 md:p-8 flex flex-col items-center text-center gap-4">
            <div className="w-14 h-14 rounded-full bg-error/10 flex items-center justify-center">
              <Icon name="AlertTriangle" size={28} className="text-error" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-1">Insufficient Balance</h3>
              <p className="text-sm text-muted-foreground">
                You don't have enough minutes to make a test call. Please recharge your account to continue.
              </p>
            </div>
            <button
              onClick={() => { onClose(); navigate('/settings'); }}
              className="px-6 py-2.5 bg-primary text-primary-foreground rounded-md font-medium hover:bg-primary/90 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              Go to Settings
            </button>
          </div>
        )}

        {/* Success Message */}
        {!balanceLoading && hasBalance && showSuccess && (
          <div className="m-4 md:m-6 p-4 bg-success/10 border border-success rounded-lg flex items-center gap-3">
            <Icon name="CheckCircle" size={20} className="text-success" />
            <p className="text-sm font-medium text-success">Test call initiated successfully</p>
          </div>
        )}

        {/* Form */}
        {!balanceLoading && hasBalance && !showSuccess && (
          <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4">
            {/* Test Phone Number */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Test Phone Number <span className="text-error">*</span>
              </label>
              <input
                type="tel"
                name="testPhoneNumber"
                value={formData?.testPhoneNumber}
                onChange={handleChange}
                required
                placeholder="Enter 10-digit phone number"
                pattern="[0-9]{10}"
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Customer Name */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Customer Name <span className="text-error">*</span>
              </label>
              <input
                type="text"
                name="customerName"
                value={formData?.customerName}
                onChange={handleChange}
                required
                placeholder="Enter customer name"
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Order ID */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Order ID
              </label>
              <input
                type="text"
                name="orderId"
                value={formData?.orderId}
                onChange={handleChange}
                placeholder="Enter order ID"
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Email ID */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Email ID
              </label>
              <input
                type="email"
                name="emailId"
                value={formData?.emailId}
                onChange={handleChange}
                placeholder="Enter email address"
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Registered Mobile Number */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Registered Mobile Number
              </label>
              <input
                type="tel"
                name="registeredMobileNumber"
                value={formData?.registeredMobileNumber}
                onChange={handleChange}
                placeholder="Enter registered mobile number"
                pattern="[0-9]{10}"
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Product Bought */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Product Bought
              </label>
              <input
                type="text"
                name="productBought"
                value={formData?.productBought}
                onChange={handleChange}
                placeholder="Enter product name"
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Order Date */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Order Date
              </label>
              <input
                type="date"
                name="orderDate"
                value={formData?.orderDate}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Submit Button */}
            <div className="pt-2">
              <button
                type="submit"
                className="w-full px-4 py-2.5 bg-primary text-primary-foreground rounded-md font-medium hover:bg-primary/90 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                Make Test Call
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default TestCallModal;
